/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2026 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import * as React from "react";
import styled from "styled-components";
import { Heading22 } from "components/shared/Heading";
import { Text18 } from "components/shared/Text";
import blockedUserIllustration from "components/UseCases/images/blocked-user.png";
import { devices } from "components/shared/Styles";
import { SpectrumCssWrapper } from "components/shared/SpectrumCss";
import { AppHeader, AppHeaderHeight } from "components/shared/AppHeader";
import { useCurrentUser } from "actions/CurrentUserActions";
import { CurrentUserLoadingState } from "reducers/CurrentUserReducer";
import AppLoading from "components/AppLoading";
import { AppPath } from "components/AppPath";
import { Redirect } from "react-router-dom";

const Background = styled.div`
  padding-top: ${AppHeaderHeight};
  position: fixed;
  left: 0;
  right: 0;
  top: 0;
  background-color: var(--spectrum-semantic-cta-background-color-default);
  display: flex;
  justify-content: center;
  overflow: scroll;
  height: calc(100% - ${AppHeaderHeight});
`;

const Content = styled.div`
  position: relative;
  top: 200px;
  height: 500px;
  width: 360px;
  border-radius: 16px;
  overflow: hidden;
  background-color: var(--spectrum-global-color-static-gray-300);

  ${devices.small} {
    top: 100px;
  }
`;

const Illustration = styled.div`
  position: absolute;
  top: 0;
  height: 265px;
  width: 100%;
  background-image: url("${blockedUserIllustration}");
  background-color: var(--spectrum-global-color-static-gray-300);
  background-position: center;
  background-repeat: no-repeat;
  background-size: contain;
`;

const MessageContainer = styled.div`
  position: absolute;
  bottom: 0;
  height: 243px;
  width: 100%;
  background-color: var(--spectrum-global-color-static-white);
`;

const Message = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 100%;
  margin: 0 50px;
`;

const BlockedRegionPage: React.FC = () => {
  const { anonymousAllowedCurrentUser, currentUserLoadingState } =
    useCurrentUser();

  if (currentUserLoadingState === CurrentUserLoadingState.LOADING) {
    return <AppLoading />;
  }

  // If the user is loaded and not blocked, redirect to the root page
  // This is to prevent the user from seeing the blocked region page if they were blocked and logged out.
  if (
    currentUserLoadingState === CurrentUserLoadingState.LOADED &&
    (!anonymousAllowedCurrentUser ||
      !anonymousAllowedCurrentUser.hasBlockedCountryCodeForUserProfile)
  ) {
    return <Redirect to={AppPath.ROOT} />;
  }

  return (
    <SpectrumCssWrapper>
      <AppHeader />
      <Background>
        <Content>
          <Illustration data-testid="blocked-user-illustration" />
          <MessageContainer>
            <Message>
              <Heading22>{I18n.t("blocked_region.heading")}</Heading22>
              <Text18 aria-live="polite" role="alert">
                {I18n.t("blocked_region.body")}
              </Text18>
            </Message>
          </MessageContainer>
        </Content>
      </Background>
    </SpectrumCssWrapper>
  );
};

export default BlockedRegionPage;
